/**
 * Auto checklist keys for hotels / meals — mirrors iOS TripTaskTriggerService (#150).
 */

import type { TripTaskItem } from '../types/client';

export const HOTEL_CONFIRMATION_TASK_PREFIX = 'hotel.confirmation.';

export function hotelConfirmationTaskKey(hotelStayId: string): string {
  return `${HOTEL_CONFIRMATION_TASK_PREFIX}${hotelStayId}`;
}

export function hotelStayHasConfirmationNumber(params: {
  confirmations?: Array<string | { confirmationNumber?: string | null }> | null;
  confirmationNumber?: string | null;
}): boolean {
  if (params.confirmationNumber?.trim()) return true;
  for (const entry of params.confirmations ?? []) {
    if (typeof entry === 'string' && entry.trim()) return true;
    if (
      entry &&
      typeof entry === 'object' &&
      typeof entry.confirmationNumber === 'string' &&
      entry.confirmationNumber.trim()
    ) {
      return true;
    }
  }
  return false;
}

function matchesHotelConfirmationKey(task: TripTaskItem, key: string): boolean {
  if (task.id === key) return true;
  return Boolean(task.taskKey && task.taskKey === key);
}

/**
 * Remove the `hotel.confirmation.{stayId}` TripTask when a confirmation number is present.
 * No-op when confirmation is empty.
 */
export function clearHotelConfirmationTripTaskOnEnrich(params: {
  taskItems: TripTaskItem[] | null | undefined;
  hotelStayId: string;
  confirmationNumber?: string | null;
  confirmations?: Array<string | { confirmationNumber?: string | null }> | null;
}): TripTaskItem[] {
  const items = params.taskItems ?? [];
  if (
    !hotelStayHasConfirmationNumber({
      confirmationNumber: params.confirmationNumber,
      confirmations: params.confirmations,
    })
  ) {
    return items;
  }
  const key = hotelConfirmationTaskKey(params.hotelStayId);
  return items.filter((t) => !matchesHotelConfirmationKey(t, key));
}

/** Remove hotel confirmation task when the stay is deleted. */
export function removeHotelConfirmationTripTask(params: {
  taskItems: TripTaskItem[] | null | undefined;
  hotelStayId: string;
}): TripTaskItem[] {
  const key = hotelConfirmationTaskKey(params.hotelStayId);
  return (params.taskItems ?? []).filter((t) => !matchesHotelConfirmationKey(t, key));
}

/**
 * Ensure or clear `hotel.confirmation.{stayId}` after add/edit.
 * Skips when reservationId is set (in-app booking).
 */
export function syncHotelConfirmationTripTask(params: {
  taskItems: TripTaskItem[] | null | undefined;
  hotelStayId: string;
  hotelName: string;
  confirmations?: Array<string | { confirmationNumber?: string | null }> | null;
  reservationId?: string | null;
}): TripTaskItem[] {
  const key = hotelConfirmationTaskKey(params.hotelStayId);
  const items = params.taskItems ?? [];
  const reservationId = params.reservationId?.trim() ?? '';
  if (reservationId) {
    return items.filter((t) => !matchesHotelConfirmationKey(t, key));
  }
  if (
    hotelStayHasConfirmationNumber({
      confirmations: params.confirmations,
    })
  ) {
    return items.filter((t) => !matchesHotelConfirmationKey(t, key));
  }
  if (items.some((t) => matchesHotelConfirmationKey(t, key))) {
    return items;
  }
  const title = `Add confirmation for ${params.hotelName.trim() || 'hotel'}`;
  const maxOrder = items.reduce((m, t) => Math.max(m, t.sortOrder ?? 0), -1);
  return [
    ...items,
    {
      id: key,
      title,
      isDone: false,
      sortOrder: maxOrder + 1,
      taskKey: key,
    },
  ];
}
